import { useMemo } from 'react';
import { useBooleanConditions } from '../../../hooks/useBooleanConditions.tsx';
import { type IRuntimeWidgetAdvancedOption } from '../advanced-select-common/IAdvancedSelectWidget.ts';
import { getUniqueOptionValue } from '../advanced-select-common/utils/geUniqueOptionValue.ts';
import { type IAdvancedSingleSelectWidget } from './IAdvancedSingleSelectWidget.ts';

interface IAdvancedSingleSelectWidgetInput {
  widget: IAdvancedSingleSelectWidget;
  name: string;
  preview?: boolean;
}

/**
 * Hook per ottenere le opzioni e il dataSource per l'AdvancedSingleSelect
 *
 * Fare riferimento a {@link useSingleSelectWidgetOptions} per le select singole
 * semplici
 */
export function useAdvancedSingleSelectWidgetOptions({
  widget,
  name,
  preview,
}: IAdvancedSingleSelectWidgetInput): IRuntimeWidgetAdvancedOption[] {
  const optionsWithConditions = useMemo(() => {
    return widget.options.map(option => ({
      option,
      visible: option.visible,
      enabled: option.enabled,
    }));
  }, [widget.options]);

  const visibles = useBooleanConditions(
    optionsWithConditions.map(o => o.visible),
    { name, preview },
  );

  const enableds = useBooleanConditions(
    optionsWithConditions.map(o => o.enabled),
    { name, preview },
  );

  const dataSource = useMemo(() => {
    // Calcoliamo le opzioni da mostrare nella tabella, pre-calcolando
    // se sono disabilitate e verificando se sono _visibili_
    const processedOptions = widget.options
      .map((option, i) => ({
        ...option,
        disabled: !enableds[i].result,
      }))
      .filter((_, index) => visibles[index].result);

    return processedOptions.map(option => {
      const rowData: IRuntimeWidgetAdvancedOption = {
        ...option,
        id: getUniqueOptionValue(widget, option),
        disabled: option.disabled,
      };

      return rowData;
    });
  }, [widget.columns, widget.uniqueValueColumnId, widget, enableds, visibles]);

  return dataSource;
}
